import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import BackgroundImage from "./BackgroundImage";

const ParticipationCriteria = () => {
  const [formData, setFormData] = useState({
    name: "",
    role: "",
    otherRole: "",
    linkedin: "",
    email: "",
    teamStatus: ""
  });

  useEffect(() => {
    const id = "tally-embed-script";
    const existing = document.getElementById(id) as HTMLScriptElement | null;
    if (existing && (window as any).Tally) {
      (window as any).Tally.loadEmbeds();
      return;
    }
    const s = document.createElement("script");
    s.id = id;
    s.src = "https://tally.so/widgets/embed.js";
    s.async = true;
    s.onload = () => {
      try { (window as any).Tally?.loadEmbeds(); } catch {}
    };
    document.body.appendChild(s);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRoleChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      role: value,
      otherRole: value === "Other" ? prev.otherRole : ""
    }));
  };

  const handleTeamStatusChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      teamStatus: value
    }));
  };

  const normalizeLinkedInUrl = (url: string) => {
    if (!url) return "";
    
    // Remove any whitespace
    const cleanUrl = url.trim();
    
    // Extract the username part and return in linkedin.com/in/username format
    let username = "";
    
    // If it starts with https://www.linkedin.com/in/ or https://linkedin.com/in/
    if (cleanUrl.includes("linkedin.com/in/")) {
      const parts = cleanUrl.split("linkedin.com/in/");
      if (parts.length > 1) {
        username = parts[1].split("/")[0]; // Get username, remove any trailing parts
      }
    }
    // If it starts with www.linkedin.com/in/
    else if (cleanUrl.startsWith("www.linkedin.com/in/")) {
      username = cleanUrl.replace("www.linkedin.com/in/", "").split("/")[0];
    }
    // If it starts with linkedin.com/in/
    else if (cleanUrl.startsWith("linkedin.com/in/")) {
      username = cleanUrl.replace("linkedin.com/in/", "").split("/")[0];
    }
    // If it's just the username
    else {
      username = cleanUrl;
    }
    
    // Return in the format linkedin.com/in/username
    return username ? `linkedin.com/in/${username}` : cleanUrl;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Simple validation
    if (!formData.name || !formData.role || !formData.email || !formData.teamStatus) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.role === "Other" && !formData.otherRole) {
      toast.error("Please specify your role");
      return;
    }

    // Prepare data for Tally form with proper URL encoding
    const roleValue = formData.role === "Other" ? formData.otherRole : formData.role;
    const teamValue = formData.teamStatus === "have-team" ? "Yes" : "No";
    const normalizedLinkedIn = normalizeLinkedInUrl(formData.linkedin);
    
    const tallyUrl = `https://tally.so/r/mOeo5R?name=${encodeURIComponent(formData.name)}&role=${encodeURIComponent(roleValue)}&email=${encodeURIComponent(formData.email)}&linkedin=${encodeURIComponent(normalizedLinkedIn)}&team=${encodeURIComponent(teamValue)}`;
    
    console.log("Form data being sent:", {
      name: formData.name,
      role: roleValue,
      email: formData.email,
      linkedin: normalizedLinkedIn,
      team: teamValue
    });
    
    // Open Tally form with pre-filled data
    window.open(tallyUrl, '_blank');
    
    toast.success("Opening registration form with your details!");

    // Reset form
    setFormData({
      name: "",
      role: "",
      otherRole: "",
      linkedin: "",
      email: "",
      teamStatus: ""
    });
  };

  return (
    <section id="participation-criteria" className="w-full py-16 md:py-24" style={{ backgroundColor: '#F2F3EE' }}>
      <div className="container px-4 sm:px-6 lg:px-8 mx-auto">
        <div className="mb-10 md:mb-14">
          <div className="flex justify-center md:justify-start items-center gap-4 mb-8 md:mb-6">
            <div 
              className="inline-flex items-center justify-center px-4 py-2 rounded-full"
              style={{
                backgroundColor: 'rgba(242, 76, 39, 0.1)',
                border: '1px solid rgba(242, 76, 39, 0.2)',
                color: 'rgba(242, 76, 39, 1)'
              }}
            >
              <span 
                className="inline-flex items-center justify-center w-5 h-5 rounded-full text-white mr-2"
                style={{ backgroundColor: 'rgba(242, 76, 39, 1)' }}
              >
                2
              </span>
              <span>Registration</span>
            </div>
          </div>
          
         <h2 
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold mb-4 pb-2 leading-[1.2] text-center md:text-left"
            style={{
              background: 'linear-gradient(135deg, rgba(242, 76, 39, 1) 0%, rgba(31, 31, 30, 1) 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent'
            }}
          >
            <span className="md:hidden">Join ai<span style={{ color: 'rgba(242, 76, 39, 1)' }}>4</span>hack Now</span>
            <span className="hidden md:inline">Join ai<span style={{ color: 'rgba(242, 76, 39, 1)' }}>4</span>hack Right Now</span>
          </h2>
        </div>
        
        <div className="grid grid-cols-1 gap-6 md:gap-8 lg:grid-cols-2 items-start">
          {/* Left Card - Registration Form */}
          <div className="opacity-0 animate-fade-in" style={{ animationDelay: "0.3s", animationFillMode: "forwards" }}>
            <Card className="rounded-2xl sm:rounded-3xl overflow-hidden shadow-elegant">
              <CardHeader className="relative h-20 sm:h-24 p-4 sm:p-6 flex flex-row items-center justify-between flex-shrink-0 overflow-hidden">
                <BackgroundImage 
                  src="/lovable-uploads/4c8d0304-78fa-4988-85a8-28e255469c97.png"
                  className="absolute inset-0"
                  style={{
                    backgroundSize: "cover",
                    backgroundPosition: "center"
                  }}
                />
                <h3 className="text-xl sm:text-2xl font-display text-white font-bold relative z-10">
                  Join ai<span style={{ color: 'rgba(242, 76, 39, 1)' }}>4</span>hack
                </h3>
                <div className="inline-block px-3 sm:px-4 py-1 sm:py-2 border border-white text-white rounded-full text-xs relative z-10">
                  Register Now
                </div>
              </CardHeader>
              
              <CardContent 
                className="p-0"
                style={{
                  backgroundColor: "#FFFFFF"
                }}
              >
                <iframe
                  data-tally-src="https://tally.so/embed/mOeo5R?hideTitle=1&transparentBackground=1&dynamicHeight=1"
                  loading="lazy"
                  width="100%"
                  height="520"
                  scrolling="no"
                  frameBorder="0"
                  marginHeight={0}
                  marginWidth={0}
                  title="Join ai4hack Registration Form"
                  className="rounded-b-2xl sm:rounded-b-3xl"
                />
              </CardContent>
            </Card>
          </div>

          {/* Right Card - Criteria */}
          <div className="opacity-0 animate-fade-in" style={{ animationDelay: "0.6s", animationFillMode: "forwards" }}>
            <Card className="rounded-2xl sm:rounded-3xl overflow-hidden shadow-elegant">
              <CardHeader className="relative h-20 sm:h-24 p-4 sm:p-6 flex items-center justify-center flex-shrink-0 overflow-hidden">
                <BackgroundImage 
                  src="/lovable-uploads/566a49a2-5553-4a8f-9fb1-2bd20c7ab011.png"
                  className="absolute inset-0"
                  style={{
                    backgroundSize: "cover",
                    backgroundPosition: "center"
                  }}
                />
                <h3 className="text-xl sm:text-2xl font-display text-white font-bold text-center relative z-10">
                  What We're Looking For
                </h3>
              </CardHeader>
              
              <CardContent 
                className="p-6 sm:p-8"
                style={{
                  backgroundColor: "#FFFFFF"
                }}
              >
                <div className="space-y-4 sm:space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-dark-900 flex items-center justify-center flex-shrink-0">
                      <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 5L5 9L13 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="p-3 rounded-lg bg-gray-50/80 backdrop-blur-sm border border-gray-100">
                        <span className="font-semibold text-base">Willingness to build AI products</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-dark-900 flex items-center justify-center flex-shrink-0">
                      <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 5L5 9L13 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="p-3 rounded-lg bg-gray-50/80 backdrop-blur-sm border border-gray-100">
                        <span className="font-semibold text-base">Background in IT or product development</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-dark-900 flex items-center justify-center flex-shrink-0">
                      <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 5L5 9L13 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="p-3 rounded-lg bg-gray-50/80 backdrop-blur-sm border border-gray-100">
                        <span className="font-semibold text-base">A team up to 4 people, solo founders welcome</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-dark-900 flex items-center justify-center flex-shrink-0">
                      <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 5L5 9L13 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="p-3 rounded-lg bg-gray-50/80 backdrop-blur-sm border border-gray-100">
                        <span className="font-semibold text-base">If you don't have a team, we'll definitely match you with other awesome people</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ParticipationCriteria;
